import type { DashboardBoard, LayoutEntry } from '@palaccount/contracts';
import { GripVertical, EyeOff, Maximize2, Minimize2, Plus, RefreshCw, X } from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmBox } from '@/components/confirm-box';
import { Button } from '@/components/ui/button';
import { hasModule, useCan, useCapabilities } from '@/lib/permissions';
import { cn } from '@/lib/utils';

import { useLayout, useResetLayout, useSaveLayout } from './api';
import {
  addWidget,
  allAdded,
  boardIsEmpty,
  catalogTiles,
  cycleSize,
  hideWidget,
  moveWidget,
  resolveLayout,
  visibleWidgets,
  type WidgetDef,
} from './board-engine';

/**
 * DSH-00 — the shared customizable board: filter/action bar (band 1), the
 * inline add-widget panel (band 3), the empty state (band 4) and the widget
 * grid (band 5). The three dashboards are this component with three
 * configurations (§14.1.0).
 *
 * The grid is the spec's own `repeat(auto-fit, minmax(210px, 1fr))` with an
 * 18px gap (BR-DSH-00-10, BR-DSH-00-12): sm spans one track, md two, lg the whole row.
 * Drag-onto-swap only (BR-DSH-00-23), resize cycles allowed sizes
 * (BR-DSH-00-11), hide feeds the panel (BR-DSH-00-05), reset restores the
 * delivered default behind the §14.1.3 confirmation.
 */
export function Board({
  board,
  catalog,
  filters,
  stamp,
  disclosure,
  renderWidget,
}: {
  board: DashboardBoard;
  catalog: readonly WidgetDef[];
  /**
   * The board's own filter dropdowns (band 1, leading edge).
   *
   * Their choices persist for the browser session and reset on the next
   * sign-in — BR-DSH-00-16, built to §14.9 question 2's proposed default.
   * The mechanism is not here: each page holds its filter state in
   * `useSessionFilter` (`lib/dashboard-filters.ts`, where the rule and its
   * provenance are written out), and this component renders whatever it is
   * handed. The note that stood here recorded the rule as unbuilt and
   * "left for the owner with the rest of its bracket"; the bracket's own
   * section says "no build is blocked", which is the reading the view-all
   * link had already established — see `dsh00-filter-persistence.test.tsx`.
   */
  filters?: ReactNode;
  /** `Updated 09:42` or `As of …` — the §14.1.3 freshness stamp. */
  stamp: { text: string; refresh: () => void };
  /** DSH-04 band 2 — the consolidation line, when conversion happened. */
  disclosure?: ReactNode;
  renderWidget: (id: string) => ReactNode;
}) {
  const { t } = useTranslation();
  const layoutQuery = useLayout(board);
  const save = useSaveLayout(board);
  const reset = useResetLayout(board);
  const [panelOpen, setPanelOpen] = useState(false);
  /**
   * GEN-209 (spec line 4709) lists seven acts that must raise the shared
   * dialog, and "RESETTING A LAYOUT" is the last of them, named outright.
   * This screen asked with `window.confirm`, which BR-PERM-15 (spec line
   * 35168) forbids — "every irreversible action must use one of these two
   * patterns; a new pattern must not be invented", and browser chrome is
   * neither of the two.
   */
  const [resetAsk, setResetAsk] = useState(false);
  const dragging = useRef<string | null>(null);

  /**
   * BR-DSH-00-17: a widget whose data the viewer may not read is not offered
   * at all — not on the board, not in the catalogue.
   *
   * BR-DSH-00-18, BR-DSH-00-19 add the company's half in the SAME pass, because they
   * describe the same list: "the add-widget catalogue must be ONE list. Every
   * widget in it must be offered to every user whose permissions
   * (BR-DSH-00-17) and module flags (BR-DSH-00-18) allow it." Filtering here
   * rather than in the three pages is what makes the catalogue obey it too —
   * `catalogTiles` and `defaults` both read `permitted`, so a widget the
   * company cannot have is absent from the tile panel as well as the grid, and
   * nobody can add something that would then refuse to render.
   *
   * §1.5.2's order is visible in the two lines: permission first, then the
   * module. While /api/me is still in flight both are answered
   * OPTIMISTICALLY — `useCan` for the reason it documents, and the module for
   * the same reason `RequirePermission` renders its children through
   * `caps.loading`: the server refuses regardless, so the worst case is a tile
   * that disappears a moment later rather than legal work hidden from someone
   * entitled to it.
   */
  const canSales = useCan('sales.view');
  const canPurchasing = useCan('purchasing.view');
  const caps = useCapabilities();
  const permitted = useMemo(() => {
    const held = new Set<string>([
      ...(canSales ? ['sales'] : []),
      ...(canPurchasing ? ['purchasing'] : []),
    ]);
    return catalog.filter(
      (w) =>
        (w.needs ?? []).every((area) => held.has(area)) &&
        (w.module === undefined || caps.loading || hasModule(caps, w.module)),
    );
  }, [catalog, canSales, canPurchasing, caps]);

  const layout = useMemo(
    () => resolveLayout(permitted, layoutQuery.data ?? []),
    [permitted, layoutQuery.data],
  );

  /**
   * `layout` above is resolveLayout(permitted, layoutQuery.data ?? []) — so
   * before the stored layout arrives it is the DELIVERED DEFAULT, not this
   * user's. Every arrangement control computes its write FROM that value, so
   * pressing one in that window persists a rearrangement of the default over
   * whatever the user had actually saved. The controls wait until the layout
   * is known; the mutations cancel any outstanding read (see api.ts) so a slow
   * GET cannot land afterwards and undo the write.
   */
  const layoutKnown = !layoutQuery.isPending;
  /**
   * ERR-DSH-02-10 — "The saved layout could not be written. Proposed message:
   * *Your layout could not be saved. Your changes will be lost when you leave
   * this page.*" — the proposed sentence, adopted verbatim as
   * `dash.layoutSaveFailed`.
   *
   * THE HOLE THIS CLOSES: `useSaveLayout` throws on a non-200 and nothing
   * read `save.isError`, so the widget the user just moved stayed where they
   * put it, LOOKED saved, and was gone on the next load — the one state where
   * the screen and the server disagree was the one the screen said nothing
   * about. (An earlier wave recorded the gap here and left it for want of the
   * dictionary string; the string exists now, and the branch is these lines.)
   *
   * The RESET path shares the sentence: a failed DELETE is equally a layout
   * write that did not happen. And it is `isError`, not a latch: React Query
   * clears it on the next mutation, so a save that then succeeds withdraws
   * the report — it describes the CURRENT disagreement, not history.
   */
  const layoutWriteFailed = save.isError || reset.isError;
  const apply = (next: LayoutEntry[]) => {
    if (!layoutKnown) return;
    save.mutate(next);
  };
  const defs = new Map(permitted.map((w) => [w.id, w]));
  const tiles = catalogTiles(permitted, layout);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {filters}
        <div className="ms-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{stamp.text}</span>
          <Button variant="outline" size="icon" aria-label={t('dash.refresh')} onClick={stamp.refresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            disabled={!layoutKnown}
            // §14.1.3: reset must ask first — it throws away work. GEN-208:
            // the click OPENS the dialog and does nothing else; the reset
            // itself waits for the dialog's own verb.
            onClick={() => setResetAsk(true)}
          >
            {t('dash.resetLayout')}
          </Button>
          <Button disabled={!layoutKnown} onClick={() => setPanelOpen((open) => !open)}>
            <Plus className="me-1 h-4 w-4" />
            {t('dash.addWidget')}
          </Button>
        </div>
      </div>

      {/*
        ADM-02 §8's shared box, two outcomes. `dash.resetConfirm` is already a
        QUESTION ("Reset this board to its delivered layout?") so it is
        GEN-205's title unchanged; the body is the new half — GEN-205 asks the
        body to say WHAT WILL HAPPEN, which the question alone does not.

        The body deliberately does NOT say "this cannot be undone": a reset
        board can be rearranged again, so writing GEN-58's sentence here would
        be false. That clause is `DeleteConfirmBox`'s, and this is not a delete.
      */}
      <ConfirmBox
        open={resetAsk}
        title={t('dash.resetConfirm')}
        body={t('dash.resetConfirmBody')}
        confirmLabel={t('dash.resetLayout')}
        onConfirm={() => {
          setResetAsk(false);
          reset.mutate();
        }}
        onCancel={() => setResetAsk(false)}
      />

      {/* ERR-DSH-02-10 — the failed layout write, reported where the layout is. */}
      {layoutWriteFailed ? (
        <p role="alert" className="text-sm text-destructive">
          {t('dash.layoutSaveFailed')}
        </p>
      ) : null}

      {disclosure}

      {panelOpen ? (
        <div className="rounded-2xl border bg-card p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold">{t('dash.addWidget')}</p>
              <p className="text-sm text-muted-foreground">{t('dash.addWidgetSub')}</p>
            </div>
            <Button variant="ghost" size="icon" aria-label={t('dash.closePanel')} onClick={() => setPanelOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          {/* ERR-DSH-02-03 — the add panel opened with everything already on the board. */}
          {allAdded(permitted, layout) ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('dash.allAdded')}</p>
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-3">
              {tiles.map((widget) => (
                <button
                  key={widget.id}
                  type="button"
                  disabled={!layoutKnown}
                  className="rounded-xl border p-3 text-start hover:border-primary disabled:opacity-50"
                  onClick={() => apply(addWidget(layout, widget))}
                >
                  <p className="truncate text-sm font-medium">{t(widget.titleKey)}</p>
                  <p className="text-xs text-muted-foreground">{t(`dash.cat_${widget.category}`)}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/*
        ERR-DSH-00-02 / ERR-DSH-02-02 — the dashed empty-state card. The
        prototype's defect was not that the card was missing but that its BODY
        was sales-specific and shared: a user who emptied the Inventory or Cash
        Flow board was told to start tracking their sales performance. The
        string here is about WIDGETS and says nothing about any module, so one
        shared sentence is true on all three boards.
      */}
      {boardIsEmpty(layout) ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed py-16 text-center">
          <p className="text-lg font-semibold">{t('dash.emptyTitle')}</p>
          <p className="max-w-[42ch] text-sm text-muted-foreground">{t('dash.emptyBody')}</p>
          <Button onClick={() => setPanelOpen(true)}>
            <Plus className="me-1 h-4 w-4" />
            {t('dash.addWidget')}
          </Button>
        </div>
      ) : (
        <div
          className="grid items-start"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 18 }}
        >
          {visibleWidgets(layout).map((entry) => {
            const def = defs.get(entry.id);
            if (!def) return null;
            const resizable = def.sizes.length > 1;
            return (
              <section
                key={entry.id}
                aria-label={t(def.titleKey)}
                className={cn('rounded-2xl border bg-card shadow-sm')}
                style={entry.size === 'lg' ? { gridColumn: '1 / -1' } : entry.size === 'md' ? { gridColumn: 'span 2' } : undefined}
                /*
                  ERR-DSH-02-07 — "Dragged a widget outside the grid: the drag
                  is abandoned and the widget snaps back to where it was."
                  DSH-02 is the only board that states it; the shape below is
                  shared, so the other two get it for free. That is what this
                  shape GIVES rather than what
                  it checks: `onDragOver`/`onDrop` live on the WIDGET, so
                  anywhere else in the document is not a drop target, the
                  browser rejects the drop, and no `apply` runs. Nothing moved,
                  so nothing has to snap back — the layout was never written.
                  BR-DSH-00-23's drag-onto-swap is the same fact read forwards:
                  a widget is the only legal destination.
                */
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragging.current && dragging.current !== entry.id) {
                    apply(moveWidget(layout, dragging.current, entry.id));
                  }
                  dragging.current = null;
                }}
              >
                <header className="flex items-center gap-1 border-b px-3 py-2">
                  <span
                    draggable
                    role="button"
                    aria-label={t('dash.dragHandle')}
                    className="cursor-grab text-muted-foreground"
                    onDragStart={() => {
                      dragging.current = entry.id;
                    }}
                  >
                    <GripVertical className="h-4 w-4" />
                  </span>
                  <h3 className="min-w-0 flex-1 truncate text-sm font-medium">{t(def.titleKey)}</h3>
                  {resizable ? (
                    <button
                      type="button"
                      aria-label={t('dash.resize')}
                      disabled={!layoutKnown}
                      className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
                      onClick={() => apply(cycleSize(layout, entry.id, def.sizes))}
                    >
                      {entry.size === 'lg' ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    aria-label={t('dash.hide')}
                    disabled={!layoutKnown}
                    className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    onClick={() => apply(hideWidget(layout, entry.id))}
                  >
                    <EyeOff className="h-3.5 w-3.5" />
                  </button>
                </header>
                <div className="p-3" style={def.category !== 'kpi' ? { minHeight: entry.size === 'lg' ? 240 : 200 } : undefined}>
                  {renderWidget(entry.id)}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
